import { Compass, Loader2, MessageSquare, Moon, PhoneCall, SquareCheckBig, Sun, Trash2 } from 'lucide-react';
import { brandColors } from '@/design';
import { SESSION_LABELS } from '../../utils/sessionDisplay';
import type { Session, SessionType } from '../../types';

export function getConversationSessionDate(session: Session): Date {
  if (session.startedAt?.toDate) return session.startedAt.toDate();
  return new Date((session.startedAt as unknown as { seconds: number }).seconds * 1000);
}

export function getConversationSessionTitle(session: Session): string {
  const title = session.outputs?.sessionTitle?.trim();
  if (title) return title;

  const taskTitle = session.outputs?.taskTitle?.trim();
  if (taskTitle) return taskTitle;

  return SESSION_LABELS[session.type];
}

export function getConversationSessionSummary(session: Session): string | null {
  const summary = session.outputs?.sessionSummary?.trim();
  if (summary) return summary;

  const reflectionSummary = session.outputs?.reflectionSummary?.trim();
  if (reflectionSummary) return reflectionSummary;

  const dailyIntention = session.outputs?.dailyIntention?.trim();
  if (dailyIntention) return dailyIntention;

  const patternObservation = session.outputs?.patternObservation?.trim();
  if (patternObservation) return patternObservation;

  return null;
}

function formatTime(date: Date): string {
  return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

function formatDuration(durationSeconds?: number): string | null {
  if (!durationSeconds || durationSeconds <= 0) return null;
  if (durationSeconds < 60) return '<1 min';
  return `${Math.round(durationSeconds / 60)} min`;
}

function SessionTypeIcon({ type }: { type: SessionType }) {
  switch (type) {
    case 'onboarding':
      return <Compass size={16} />;
    case 'morning_checkin':
      return <Sun size={16} />;
    case 'evening_checkin':
      return <Moon size={16} />;
    case 'task_planning':
      return <SquareCheckBig size={16} />;
    case 'coaching':
      return <PhoneCall size={16} />;
    default:
      return <MessageSquare size={16} />;
  }
}

interface ConversationSessionCardProps {
  session: Session;
  onOpen: (sessionId: string) => void;
  onAnalyze: () => void;
  isAnalyzing: boolean;
  onDelete: () => void;
}

export default function ConversationSessionCard({
  session,
  onOpen,
  onAnalyze,
  isAnalyzing,
  onDelete,
}: ConversationSessionCardProps) {
  const startedAt = getConversationSessionDate(session);
  const duration = formatDuration(session.durationSeconds);
  const title = getConversationSessionTitle(session);
  const summary = getConversationSessionSummary(session);
  const statusLabel = session.status === 'completed'
    ? 'Completed'
    : session.status === 'abandoned'
      ? 'Abandoned'
      : session.status === 'failed'
        ? 'Failed'
      : session.status === 'extracting'
        ? 'Processing'
        : isAnalyzing
          ? 'Analyzing'
          : 'Active';

  const statusStyles = session.status === 'completed'
    ? { background: 'var(--color-success-subtle)', color: 'var(--color-success)' }
    : session.status === 'abandoned'
      ? { background: 'var(--color-error-subtle)', color: 'var(--color-error)' }
      : session.status === 'failed'
        ? { background: 'var(--color-error-subtle)', color: 'var(--color-error)' }
      : { background: 'var(--color-surface)', color: 'var(--color-text-secondary)' };

  return (
    <div
      className="flank-card rounded-xl p-4 text-left transition-colors hover:bg-[var(--color-surface)] cursor-pointer"
      style={{ border: '1px solid var(--color-border)' }}
      onClick={() => onOpen(session.id)}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <div
            className="w-8 h-8 rounded-lg shrink-0 flex items-center justify-center"
            style={{ background: `${brandColors.lightBlue}22`, color: brandColors.lightBlue }}
          >
            <SessionTypeIcon type={session.type} />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-[var(--color-text-primary)] truncate">
              {title}
            </p>
            <p className="text-xs text-[var(--color-text-muted)] mt-0.5">
              {formatTime(startedAt)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span
            className="text-[10px] font-semibold px-2 py-1 rounded-full"
            style={statusStyles}
          >
            {statusLabel}
          </span>
          {session.status === 'active' && (
            <button
              onClick={(e) => { e.stopPropagation(); onAnalyze(); }}
              disabled={isAnalyzing}
              className="px-2.5 py-1 rounded-md text-[10px] font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              aria-label={`Analyze ${title}`}
              title="Analyze conversation"
              style={{
                background: isAnalyzing ? 'var(--color-surface)' : 'var(--color-brand-primary)',
                color: isAnalyzing ? 'var(--color-text-secondary)' : 'var(--flank-dark-blue)',
                border: isAnalyzing ? '1px solid var(--color-border)' : 'none',
              }}
            >
              {isAnalyzing ? (
                <span className="inline-flex items-center gap-1">
                  <Loader2 size={12} className="animate-spin" />
                  Analyzing...
                </span>
              ) : (
                'Analyze conversation'
              )}
            </button>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            className="p-1.5 rounded-md hover:bg-[var(--color-surface-elevated)] text-[var(--color-text-muted)] hover:text-[var(--color-error)] transition-colors"
            aria-label={`Delete ${title}`}
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      <div className="mt-3 flex items-center gap-3 text-xs text-[var(--color-text-secondary)]">
        {duration && <span>{duration}</span>}
        <span>{session.messageCount} {session.messageCount === 1 ? 'message' : 'messages'}</span>
      </div>
      {summary && (
        <p className="mt-2 text-xs leading-relaxed text-[var(--color-text-secondary)] line-clamp-2">
          {summary}
        </p>
      )}
    </div>
  );
}
